import { Client, IMessage } from '@stomp/stompjs';
import { useCallback, useEffect, useState } from 'react';
import { useRestClient } from '../../../core/api/RestClientContext';
import { ChatDto, SupportEvent } from '../models/support';

function getSupportWebSocketUrl(): string {
    const base = process.env.REACT_APP_BASE_URL || '';
    if (base) {
        const url = new URL(base);
        const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${url.host}/api/gs-ws`;
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/api/gs-ws`;
}

const SUPPORT_TOPIC = '/topic/support/events';

interface UseSupportChatsResult {
    chats: ChatDto[];
    connected: 'connecting' | 'connected' | 'disconnected';
    updateChat: (updated: ChatDto) => void;
}

export function useSupportChats(): UseSupportChatsResult {
    const restClient = useRestClient();
    const [chats, setChats] = useState<ChatDto[]>([]);
    const [connected, setConnected] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');

    useEffect(() => {
        restClient.get<ChatDto[]>('/support/chats').then(setChats).catch(console.error);
    }, [restClient]);

    const updateChat = useCallback((updated: ChatDto) => {
        setChats(prev => {
            const exists = prev.some(c => c.tgId === updated.tgId);
            return exists
                ? prev.map(c => c.tgId === updated.tgId ? updated : c)
                : [...prev, updated];
        });
    }, []);

    useEffect(() => {
        const stompClient = new Client({
            brokerURL: getSupportWebSocketUrl(),
            reconnectDelay: 3000,
            onConnect: () => {
                setConnected('connected');
                stompClient.subscribe(SUPPORT_TOPIC, (message: IMessage) => {
                    try {
                        const events: SupportEvent[] = JSON.parse(message.body);
                        setChats(prev => {
                            let next = prev;
                            for (const event of events) {
                                next = next.map(chat =>
                                    chat.tgId === event.tgId
                                        ? { ...chat, messages: [...chat.messages, event.message] }
                                        : chat
                                );
                            }
                            return next;
                        });
                    } catch (e) {
                        console.error('Failed to parse support events WS message', e);
                    }
                });
            },
            onStompError: (_frame) => setConnected('disconnected'),
            onWebSocketClose: () => setConnected('disconnected'),
        });

        stompClient.activate();
        return () => { stompClient.deactivate(); };
    }, []);

    return { chats, connected, updateChat };
}
